import { fmtDate } from "./ui-kit";
import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ExternalLink, FileText, Receipt, Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const money = (amt, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() }).format(amt);
const STATUS = { paid: "bg-emerald-50 text-emerald-700", open: "bg-amber-50 text-amber-700", void: "bg-muted text-muted-foreground", uncollectible: "bg-rose-50 text-rose-700", draft: "bg-muted text-muted-foreground" };

export const InvoiceHistory = ({ hasSubscription }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasSubscription) { setRows([]); return; }
    api.get("/billing/invoices").then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(apiError(e)); });
  }, [hasSubscription]);

  const openPortal = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="invoice-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /><h2 className="font-head text-lg font-bold">Invoices & receipts</h2></div>
        <Button variant="outline" className="rounded-full" onClick={openPortal} disabled={!hasSubscription || busy} data-testid="manage-billing">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Settings2 className="mr-2 h-4 w-4" />} Manage billing
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Update your card, change or cancel your plan, and download receipts. Pulled live from your billing account.</p>
      {rows === null ? <p className="mt-4 text-sm text-muted-foreground">Loading invoices…</p>
        : rows.length === 0 ? <p className="mt-4 text-sm text-muted-foreground" data-testid="invoices-empty">{hasSubscription ? "No invoices yet." : "Invoices will appear here once you subscribe."}</p>
        : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="pb-2 pr-4">Date</th><th className="pb-2 pr-4">Invoice</th><th className="pb-2 pr-4">Amount</th><th className="pb-2 pr-4">Status</th><th className="pb-2"></th></tr></thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id} className="border-t border-border" data-testid="invoice-row">
                    <td className="py-2.5 pr-4 whitespace-nowrap">{fmtDate(i.date)}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{i.number || i.id}</td>
                    <td className="py-2.5 pr-4 font-semibold">{money(i.amount, i.currency)}</td>
                    <td className="py-2.5 pr-4"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS[i.status] || "bg-muted"}`}>{i.status}</span></td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      {i.hosted_url && <a href={i.hosted_url} target="_blank" rel="noreferrer" className="mr-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="invoice-view"><ExternalLink className="h-3 w-3" /> View receipt</a>}
                      {i.pdf_url && <a href={i.pdf_url} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="invoice-pdf"><FileText className="h-3 w-3" /> PDF</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};
